// © 2025 Ananda Kumar. All rights reserved.
// Unauthorized copying of this file, via any medium, is strictly prohibited.

import { Injectable, Injector } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { catchError, Observable, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { environment } from '../environments/environment';
import { LoggingService } from '../services/logging.service';


@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    private isLoggingOut = false;

    constructor(private injector: Injector) { }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // Extract tenant key from URL path (e.g., /tenantKey/home -> tenantKey)
        // This is always available and doesn't require service initialization
        let tenantId = '';

        const pathSegments = window.location.pathname.split('/').filter(Boolean);
        if (pathSegments.length > 0) {
            const tenantKey = pathSegments[0];

            // Try to get the resolved tenant ID from sessionStorage if available
            const tokenKey = 'resolvedTenant_' + tenantKey;
            const cached = sessionStorage.getItem(tokenKey);
            if (cached) {
                try {
                    const data = JSON.parse(cached);
                    tenantId = data?.tenantId ?? ""; // Fallback to tenantKey if no ID
                } catch (e) {
                    tenantId = tenantKey;
                }
            } else {
                // If not cached yet, use the tenant key itself
                // TenantService will resolve it to ID later
                tenantId = tenantKey;
            }
        }

        let headers: { [name: string]: string | string[] } = {
            'X-Client-ID': tenantId
        };

        if (req.headers.has('X-Skip-Interceptor')) {
            const headers = req.headers.delete('X-Skip-Interceptor');
            const newReq = req.clone({ headers });
            return next.handle(newReq);
        }

        // Clone request with updated headers and enable credentials for cookie handling
        req = req.clone({
            setHeaders: headers,
            withCredentials: true  // CRITICAL: Enable cookies to be sent/received
        });

        return next.handle(req).pipe(
            catchError((error: HttpErrorResponse) => {
                // Log the error to Application Insights
                const logger = this.injector.get(LoggingService);
                logger.logException(
                    new Error(`HTTP ${error.status} - ${error.message}`),
                    {
                        url: req.url,
                        method: req.method,
                        status: error.status,
                        errorBody: JSON.stringify(error.error)
                    }
                );

                // Handle 401 Unauthorized - cookie expired or invalid
                // Handle 401 Unauthorized - cookie expired or invalid
                // Ignore 401s from auth status check or logout to prevent infinite loops
                if (error.status === 401 && !req.url.includes('auth/me') && !req.url.includes('currentUser') && !req.url.includes('logout')) {
                    if (!this.isLoggingOut) {
                        this.isLoggingOut = true;
                        const authService = this.injector.get(AuthService);
                        authService.logout().subscribe({
                            complete: () => { this.isLoggingOut = false; },
                            error: () => { this.isLoggingOut = false; }
                        });
                    }
                }

                return throwError(() => error);
            })
        );
    }
}
